import { createContext, useContext, useEffect, useId } from "react";
import type { AdminToolKey } from "@remi/enums/admin-tool-key";

type RegisteredNode = { key: AdminToolKey; parentId: string | undefined };

const registry = new Map<string, RegisteredNode>();

const AdminToolContext = createContext<string | undefined>(undefined);

function mountedChildIdsOf(parentId: string | undefined): string[] {
  const children: string[] = [];
  for (const [id, node] of registry) {
    const effectiveParentId = node.parentId !== undefined && registry.has(node.parentId) ? node.parentId : undefined;
    if (effectiveParentId === parentId) {
      children.push(id);
    }
  }
  return children;
}

// Resolves via registered parent/child links, never mount order: React fires child mount effects
// before parent effects, so "last mounted wins" would let a coarser ancestor override a more
// specific nested child. A branch with 2+ mounted children (incl. no shared ancestor at the root)
// resolves to the last unambiguous node above it — undefined at the root — rather than guess.
export const getCurrentAdminTool = (): AdminToolKey | undefined => {
  const roots = mountedChildIdsOf(undefined);
  const [rootId] = roots;
  if (roots.length !== 1 || rootId === undefined) {
    return undefined;
  }
  let currentId = rootId;
  for (;;) {
    const children = mountedChildIdsOf(currentId);
    const [onlyChildId] = children;
    if (children.length !== 1 || onlyChildId === undefined) {
      return registry.get(currentId)?.key;
    }
    currentId = onlyChildId;
  }
};

export const AdminToolProvider = ({ toolKey, children }: { toolKey: AdminToolKey; children: React.ReactNode }) => {
  const parentId = useContext(AdminToolContext);
  const id = useId();

  useEffect(() => {
    registry.set(id, { key: toolKey, parentId });
    return () => {
      registry.delete(id);
    };
  }, [id, toolKey, parentId]);

  return <AdminToolContext.Provider value={id}>{children}</AdminToolContext.Provider>;
};
